const DEFAULT_KEY = 'default';
const DEFAULT_LANG_CODE = 'en-US';
const DEFAULT_CURRENCY = 'USD';

class I18nCore {
    constructor({ httpClient, urlPrefix = '', getAppIdFallback = () => DEFAULT_KEY }) {
        this.httpClient = httpClient;
        this._urlPrefix = urlPrefix;
        this.getAppIdFallback = getAppIdFallback;
        this.langCode = DEFAULT_LANG_CODE;
        this.currency = DEFAULT_CURRENCY;
        this.currentAppKey = DEFAULT_KEY;
        this.appsConfigs = null;
        this.data = null;
    }

    get urlPrefix() {
        return typeof this._urlPrefix === 'function' ? this._urlPrefix() : this._urlPrefix;
    }

    init = async appsData => {
        try {
            this.appsConfigs = appsData;
            const prefix = this.urlPrefix || '';
            this.data = await this.httpClient.invokeHTTPRequest(`${prefix}/static/translations.json`);
            if (!this.data || this.data.isError) {
                console.error('FAILED TO LOAD FRAMWORK TRANSLATIONS FILE');
                this.data = { default: {} };
            }
            await this.initAppsTranslations(appsData);
        } catch (error) {
            console.error(error);
        }
    }

    initAppsTranslations = async (appsData) => {
        if (!Array.isArray(appsData)) return;
        await Promise.allSettled(appsData.map(this.fetchAppTraslations));
    }

    fetchAppTraslations = async (app) => {
        try {
            if (!app || !this.data || this.data[app.appId]) return;
            const prefix = this.urlPrefix || '';
            const translations = await this.httpClient.invokeHTTPRequest(`${prefix}/static/${app.appId}/translations.json`, this.httpClient.GET_REQUEST, null, null, {}, `Missing traslation file for app: ${app.appId}`);
            if (translations && !translations.isError && !(translations instanceof Error)) {
                this.data[app.appId] = translations;
            }
        } catch (error) {
            console.error(error);
        }
    }

    setLangCode = lang => this.langCode = lang
    setCurrency = currency => this.currency = currency
    setCurrentAppKey = curr => this.currentAppKey = curr;

    translate = (key, params = [], appId = null) => {
        if (!this.data || !key) {
            return key;
        }
        appId = appId || this.getCurrentAppId();

        if (this.data[appId]) {
            if (this.data[appId][key]) {
                return this.addTranslationParams(this.data[appId][key], params);
            }
        }

        if (this.data[DEFAULT_KEY] && this.data[DEFAULT_KEY][key]) {
            return this.addTranslationParams(this.data[DEFAULT_KEY][key], params);
        }

        return key;
    }

    addTranslationParams = (value, params) => {
        if (!params || !params.length) {
            return value;
        }

        const splitted = value.split('{}');
        if (splitted.length <= 1) {
            return value;
        }

        let output = ``;
        splitted.forEach((section, index) => {
            output += `${section}${params[index] || ''}`;
        });

        return output;
    }

    formatNumber = (number, langCode = this.langCode, extraOptions = {}) => {
        try {
            return new Intl.NumberFormat(langCode, extraOptions).format(number);
        } catch (error) {
            console.error(error);
            return number;
        }
    }

    formatCurrency = (number, currencyCode = this.currency, langCode = this.langCode) => {
        try {
            return new Intl.NumberFormat(langCode, { style: 'currency', currency: currencyCode }).format(number);
        } catch (error) {
            console.error(error);
            return number;
        }
    }

    formatDateTime = (date, langCode = this.langCode, extraOptions = {}) => {
        try {
            return new Intl.DateTimeFormat([langCode, DEFAULT_LANG_CODE], extraOptions).format(date);
        } catch (error) {
            console.error(error);
            return date;
        }
    }

    getCurrentAppId = () => {
        if (history.state && history.state.appId) {
            return history.state.appId;
        }

        if (this.currentAppKey) {
            return this.currentAppKey;
        }

        const fallback = this.getAppIdFallback && this.getAppIdFallback();
        if (fallback) return fallback;

        return DEFAULT_KEY;
    }

    getCurrentAppConfig = (appId) => {
        try {
            const currAppId = appId || this.getCurrentAppId();
            if (!Array.isArray(this.appsConfigs) || currAppId === DEFAULT_KEY) { return {}; }
            return this.appsConfigs.find(conf => conf.appId === currAppId) || {};
        } catch (error) {
            console.error(error);
            return {};
        }
    }

    translateDOM = elementOrArray => {
        try {
            const constructorName = elementOrArray.constructor.name;
            (Array.isArray(elementOrArray) || constructorName === 'HTMLCollection' || constructorName === 'NodeList') ?
                Array.from(elementOrArray).forEach((el) => this.processElement(el)) :
                this.processElement(elementOrArray);
        } catch (error) {
            console.error(error);
        }
    }

    processElement = element => {
        element.childNodes.forEach((node) => {
            if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) {
                node.textContent = this.translate(node.textContent);
            } else if (node.nodeType === Node.ELEMENT_NODE) {
                this.processElement(node);
            }
        });
    }
}

export function createI18n(opts) {
    return new I18nCore(opts);
}
